import { PolicyMigrationLocation, PolicyMigrationPreview, PolicyWorksheetViewDTO, WorksheetItemViewDTO } from "@swivl-mono/api-helper";
import {
  PolicyFieldCandidate,
  PolicyFieldOption,
  PolicyFieldType,
  PolicyNeedsReviewGroup,
  PolicyReviewGroup,
  PolicyReviewLocation
} from "@swivl-mono/types";
import { SelectItem } from "@swivl-mono/ui";

/**
 * Turns the WHOLE org's `PolicyMigrationPreview` (org candidates + every location's own override
 * candidates) into the single combined shape `PolicyUpdatesList`/`NeedsReviewList`/`PolicyWalkThrough`
 * (built for bulk document import's cross-location Policies review) actually render — ONE
 * `PolicyReviewGroup[]` and ONE `PolicyNeedsReviewGroup[]` for the entire tab, not one set per
 * scope. Mirrors `apps/server/src/API/DocumentIngest/DocumentIngest.PolicyGroups.ts`'s
 * `buildPolicyReviewGroups`/`buildOrgPolicyReviewGroups`/`buildPolicyNeedsReviewGroups`/
 * `buildOrgPolicyNeedsReviewGroups` field-meta join, grouping, and sort order — but COMBINES the
 * org-scope and locations-scope halves those four functions keep mutually exclusive (a document is
 * always ALL-org or ALL-locations; this preview always has BOTH at once, split per mapKey) and joins
 * against the client's own already-fetched `WorksheetItemViewDTO[]` catalog instead of the
 * server-only `PolicyFieldWorksheet` catalog.
 */

interface FieldMeta {
  label: string;
  fieldType?: PolicyFieldType;
  options?: PolicyFieldOption[];
  parentMapKey?: string;
  policyTitle: string;
  policyGroup: string;
  sortOrder: number;
  fieldIndex: number;
}

const UNKNOWN_SORT_ORDER = Number.POSITIVE_INFINITY;

// WorksheetItemViewDTO.kind -> the generic PolicyFieldType PolicyValueControl dispatches on.
// dynamic_reference/static_text/hint aren't answerable fields, so no PolicyFieldCandidate ever
// carries their mapKey — included only for exhaustiveness.
const KIND_TO_FIELD_TYPE: Record<WorksheetItemViewDTO["kind"], PolicyFieldType | undefined> = {
  additive_checkbox: "toggle",
  boolean_checkbox: "toggle",
  radio: "single-select",
  dropdown: "single-select",
  parameterized_option: "single-select",
  multi_checkbox: "multi-select",
  text_value: "text",
  number_value: "number",
  dynamic_reference: undefined,
  static_text: undefined,
  hint: undefined
};

function buildFieldMetaByMapKey(worksheets: PolicyWorksheetViewDTO[]): Map<string, FieldMeta> {
  const byMapKey = new Map<string, FieldMeta>();
  for (const worksheet of worksheets) {
    // Same fallback PolicyFieldReviewView already uses for this same optional field.
    const policyGroup = worksheet.groupLabel ?? worksheet.title;
    worksheet.items.forEach((item, fieldIndex) => {
      byMapKey.set(`${worksheet.policyKey}.${item.itemKey}`, {
        label: item.label,
        fieldType: KIND_TO_FIELD_TYPE[item.kind],
        options: item.options,
        parentMapKey: item.showIf ? `${worksheet.policyKey}.${item.showIf.itemKey}` : undefined,
        policyTitle: worksheet.title,
        policyGroup,
        sortOrder: worksheet.sortOrder,
        fieldIndex
      });
    });
  }
  return byMapKey;
}

function fieldMetaFor(byMapKey: Map<string, FieldMeta>, mapKey: string): FieldMeta {
  return (
    byMapKey.get(mapKey) ?? { label: mapKey, policyTitle: "", policyGroup: "Other", sortOrder: UNKNOWN_SORT_ORDER, fieldIndex: UNKNOWN_SORT_ORDER }
  );
}

/** `${locationId ?? "org"}:${mapKey}` — the local-only "reviewed this session" key PoliciesTab tracks
 * for the cosmetic Approve state. Exported so PoliciesTab's handlers write the exact same key this
 * adapter reads. */
export function reviewKeyFor(locationId: number | null, mapKey: string): string {
  return `${locationId ?? "org"}:${mapKey}`;
}

function sortComparator(
  byMapKey: Map<string, FieldMeta>
): (a: { sortOrder: number; policyGroup: string; mapKey: string }, b: { sortOrder: number; policyGroup: string; mapKey: string }) => number {
  return (a, b) =>
    a.sortOrder - b.sortOrder ||
    a.policyGroup.localeCompare(b.policyGroup) ||
    fieldMetaFor(byMapKey, a.mapKey).fieldIndex - fieldMetaFor(byMapKey, b.mapKey).fieldIndex ||
    a.mapKey.localeCompare(b.mapKey);
}

/** One mapKey's candidates across every scope this preview covers — at most one org candidate (a
 * scope only ever has one), any number of location candidates. */
interface MapKeyCandidates {
  org?: PolicyFieldCandidate;
  locations: { locationId: number; locationName: string; candidate: PolicyFieldCandidate }[];
}

/** Buckets every org + location candidate by mapKey — the single pass both `buildGroups` and
 * `buildNeedsReviewGroups` key off, so a mapKey's org and location facts are only ever looked up
 * once. Candidates are already `diff !== "matches"`-filtered server-side; the check here is
 * defensive, matching the old per-section builder's own defensive comment. */
function collectByMapKey(preview: Pick<PolicyMigrationPreview, "org" | "locations">): Map<string, MapKeyCandidates> {
  const byMapKey = new Map<string, MapKeyCandidates>();
  const entryFor = (mapKey: string): MapKeyCandidates => {
    let entry = byMapKey.get(mapKey);
    if (!entry) {
      entry = { locations: [] };
      byMapKey.set(mapKey, entry);
    }
    return entry;
  };
  for (const candidate of preview.org) {
    if (candidate.diff === "matches") continue;
    entryFor(candidate.mapKey).org = candidate;
  }
  for (const loc of preview.locations) {
    for (const candidate of loc.candidates) {
      if (candidate.diff === "matches") continue;
      entryFor(candidate.mapKey).locations.push({ locationId: loc.locationId, locationName: loc.name, candidate });
    }
  }
  return byMapKey;
}

/** Every `status: "valid"` location candidate turned into its `PolicyReviewLocation`, sorted by
 * name — shared by both the org-with-overrides branch and the org-less locations-only branch below. */
function toReviewLocations(entries: { locationId: number; locationName: string; candidate: PolicyFieldCandidate }[]): PolicyReviewLocation[] {
  return entries
    .map(({ locationId, locationName, candidate }) => ({
      locationId,
      locationName,
      excluded: candidate.excluded === true,
      currentValue: candidate.currentValue ?? null,
      currentValueIsDefault: candidate.currentValueIsDefault === true,
      proposedValue: candidate.proposedValue as string, // caller only passes status:"valid" candidates
      rawExtracted: candidate.rawExtracted,
      citation: candidate.source.snippet
    }))
    .sort((a, b) => a.locationName.localeCompare(b.locationName) || a.locationId - b.locationId);
}

/** The value every non-excluded location agrees on (with its shared rawExtracted/citation), or all
 * three null when they disagree — same "Varies by location" rule as the server's own
 * `unanimousProposal` (DocumentIngest.PolicyGroups.ts), for the org-less "locations" scope case only. */
function unanimousLocationProposal(
  entries: { candidate: PolicyFieldCandidate }[]
): Pick<PolicyReviewGroup, "proposedValue" | "rawExtracted" | "citation"> {
  const nonExcluded = entries.filter((e) => e.candidate.excluded !== true);
  const distinct = new Set(nonExcluded.map((e) => e.candidate.proposedValue));
  if (nonExcluded.length === 0 || distinct.size > 1) return { proposedValue: null, rawExtracted: null, citation: null };
  const [first] = nonExcluded;
  return { proposedValue: first.candidate.proposedValue, rawExtracted: first.candidate.rawExtracted, citation: first.candidate.source.snippet };
}

/**
 * Per mapKey:
 *  - org has a valid proposal -> one `scope: "org"` group, enriched with any location that ALSO has
 *    its own valid override for the same mapKey (nested in `locations[]`, expandable via the
 *    existing `AffectedLocationsDetail` panel).
 *  - org has NO valid proposal but >=1 location does -> one genuine `scope: "locations"` group.
 *    `proposedValue`/`rawExtracted`/`citation` follow the same "unanimous or null" rule
 *    `buildPolicyReviewGroups` uses server-side.
 *  - neither has one (only a needs-review candidate, or nothing) -> no `groups` row; see
 *    `buildNeedsReviewGroups`.
 */
function buildGroups(
  byMapKey: Map<string, FieldMeta>,
  candidatesByMapKey: Map<string, MapKeyCandidates>,
  reviewedKeys: Set<string>
): PolicyReviewGroup[] {
  const groups: PolicyReviewGroup[] = [];

  for (const [mapKey, entry] of candidatesByMapKey) {
    const meta = fieldMetaFor(byMapKey, mapKey);
    const org = entry.org;
    const orgCandidate = org && org.status === "valid" && org.proposedValue !== null ? org : undefined;
    const validLocations = entry.locations.filter((l) => l.candidate.status === "valid" && l.candidate.proposedValue !== null);

    if (orgCandidate) {
      const reviewed = reviewedKeys.has(reviewKeyFor(null, mapKey));
      groups.push({
        groupKey: mapKey,
        mapKey,
        label: meta.label,
        ...(meta.fieldType ? { fieldType: meta.fieldType } : {}),
        ...(meta.options ? { options: meta.options } : {}),
        ...(meta.parentMapKey ? { parentMapKey: meta.parentMapKey } : {}),
        policyTitle: meta.policyTitle,
        policyGroup: meta.policyGroup,
        sortOrder: meta.sortOrder,
        proposedValue: orgCandidate.proposedValue,
        rawExtracted: orgCandidate.rawExtracted,
        citation: orgCandidate.source.snippet,
        confidence: orgCandidate.confidence,
        status: "valid",
        applied: reviewed,
        appliedCount: reviewed ? 1 : 0,
        scope: "org",
        locations: toReviewLocations(validLocations),
        orgExcluded: orgCandidate.excluded === true,
        orgCurrentValue: orgCandidate.currentValue ?? null,
        orgCurrentValueIsDefault: orgCandidate.currentValueIsDefault === true,
        orgApplied: reviewed
      });
    } else if (validLocations.length > 0) {
      const locations = toReviewLocations(validLocations);
      const nonExcluded = locations.filter((l) => !l.excluded);
      const applied = nonExcluded.length > 0 && nonExcluded.every((l) => reviewedKeys.has(reviewKeyFor(l.locationId, mapKey)));
      const appliedCount = locations.filter((l) => reviewedKeys.has(reviewKeyFor(l.locationId, mapKey))).length;
      groups.push({
        groupKey: mapKey,
        mapKey,
        label: meta.label,
        ...(meta.fieldType ? { fieldType: meta.fieldType } : {}),
        ...(meta.options ? { options: meta.options } : {}),
        ...(meta.parentMapKey ? { parentMapKey: meta.parentMapKey } : {}),
        policyTitle: meta.policyTitle,
        policyGroup: meta.policyGroup,
        sortOrder: meta.sortOrder,
        ...unanimousLocationProposal(validLocations),
        confidence: validLocations.reduce((min, l) => Math.min(min, l.candidate.confidence), 1),
        status: "valid",
        applied,
        appliedCount,
        scope: "locations",
        locations
      });
    }
  }

  return groups.sort(sortComparator(byMapKey));
}

interface NeedsReviewDraft {
  mapKey: string;
  rawExtracted: string;
  citation: string;
  reason: string;
  citationUnverified: boolean;
  locations: { locationId: number; locationName: string }[];
}

/**
 * Org's own needs-review candidates (one card per mapKey; org only ever has one candidate per
 * mapKey) PLUS every location's needs-review candidates grouped by `mapKey + rawExtracted`
 * (locations that hit the identical unrecognized text bundle into one card) — mirrors
 * `buildOrgPolicyNeedsReviewGroups`/`buildPolicyNeedsReviewGroups` exactly. Kept as two separate
 * passes rather than one merged map: an org card and a locations card CAN legitimately coexist for
 * the same mapKey (even the same rawExtracted) — org and location legacy prose are independently
 * extracted (see CortexPolicyMigrationService.preview's per-scope extraction). `groupKey` is
 * prefixed `org::`/`locations::` so the two never collide as React keys when that happens.
 * A candidate marked `excluded` (via the same persisted "Skip this one" mechanism the main review
 * rows use, dismissed from NeedsReviewList instead of fixed) is skipped entirely — a dismissed
 * needs-review card has no proposed value to keep showing, unlike an excluded ordinary row.
 */
function buildNeedsReviewGroups(
  byMapKey: Map<string, FieldMeta>,
  preview: Pick<PolicyMigrationPreview, "org" | "locations">
): PolicyNeedsReviewGroup[] {
  const groups: PolicyNeedsReviewGroup[] = [];

  for (const candidate of preview.org) {
    if (candidate.status !== "needs-review" || !candidate.reason || candidate.excluded) continue;
    const meta = fieldMetaFor(byMapKey, candidate.mapKey);
    groups.push({
      groupKey: `org::${candidate.mapKey}::${candidate.rawExtracted}`,
      mapKey: candidate.mapKey,
      label: meta.label,
      ...(meta.fieldType ? { fieldType: meta.fieldType } : {}),
      ...(meta.options ? { options: meta.options } : {}),
      policyTitle: meta.policyTitle,
      policyGroup: meta.policyGroup,
      sortOrder: meta.sortOrder,
      rawExtracted: candidate.rawExtracted,
      citation: candidate.source.snippet,
      reason: candidate.reason,
      ...(candidate.citationUnverified ? { citationUnverified: true } : {}),
      scope: "org",
      locations: []
    });
  }

  const drafts = new Map<string, NeedsReviewDraft>();
  for (const loc of preview.locations) {
    for (const candidate of loc.candidates) {
      if (candidate.status !== "needs-review" || !candidate.reason || candidate.excluded) continue;
      const key = `${candidate.mapKey}::${candidate.rawExtracted}`;
      let draft = drafts.get(key);
      if (!draft) {
        draft = {
          mapKey: candidate.mapKey,
          rawExtracted: candidate.rawExtracted,
          citation: candidate.source.snippet,
          citationUnverified: candidate.citationUnverified === true,
          reason: candidate.reason,
          locations: []
        };
        drafts.set(key, draft);
      }
      draft.locations.push({ locationId: loc.locationId, locationName: loc.name });
    }
  }
  for (const [key, draft] of drafts) {
    const meta = fieldMetaFor(byMapKey, draft.mapKey);
    groups.push({
      groupKey: `locations::${key}`,
      mapKey: draft.mapKey,
      label: meta.label,
      ...(meta.fieldType ? { fieldType: meta.fieldType } : {}),
      ...(meta.options ? { options: meta.options } : {}),
      policyTitle: meta.policyTitle,
      policyGroup: meta.policyGroup,
      sortOrder: meta.sortOrder,
      rawExtracted: draft.rawExtracted,
      citation: draft.citation,
      reason: draft.reason,
      ...(draft.citationUnverified ? { citationUnverified: true } : {}),
      scope: "locations",
      locations: draft.locations.sort((a, b) => a.locationName.localeCompare(b.locationName) || a.locationId - b.locationId)
    });
  }

  const cmp = sortComparator(byMapKey);
  return groups.sort((a, b) => cmp(a, b) || a.rawExtracted.localeCompare(b.rawExtracted));
}

/**
 * The single entry point PoliciesTab calls — the WHOLE org's preview in, one combined
 * `{ groups, needsReview }` out, driving one `NeedsReviewList` + one `PolicyUpdatesList`/
 * `PolicyWalkThrough` instance covering org AND every location's overrides at once.
 */
export function buildPolicyReviewData(
  preview: Pick<PolicyMigrationPreview, "org" | "locations">,
  worksheets: PolicyWorksheetViewDTO[],
  reviewedKeys: Set<string>
): { groups: PolicyReviewGroup[]; needsReview: PolicyNeedsReviewGroup[] } {
  const byMapKey = buildFieldMetaByMapKey(worksheets);
  const candidatesByMapKey = collectByMapKey(preview);
  return {
    groups: buildGroups(byMapKey, candidatesByMapKey, reviewedKeys),
    needsReview: buildNeedsReviewGroups(byMapKey, preview)
  };
}

/** Every location known to have at least one override anywhere in this org's preview — the pool
 * `AffectedLocationsField`'s multi-select offers for "which locations does this apply to." */
export function buildLocationOptions(locations: PolicyMigrationLocation[]): SelectItem[] {
  return locations.map((loc) => ({ label: loc.name, value: String(loc.locationId) }));
}
